import React, { useState, useCallback } from "react";
import useSWR from "swr";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataGrid, Column, Lookup } from "devextreme-react/data-grid";
import Accordion from "devextreme-react/accordion";
import ApprovalStatusBar from "../../Components/OrderComponents/approvelStatusBar";
import renderBarStatus, {
  renderStatus,
  renderUrgency,
} from "../../Components/OrderComponents/renderConst";
import { urgencyKey } from "../../Components/OrderComponents/constants";
import Button from "devextreme-react/button";
import ChatBox from "../../Components/ChatBox";
import ProvideAssistance from "../../Components/ProvideAssistance/provideAssistance";
import RequestAssistance from "../../Components/RequestAssistance/requestAssistance";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import ActionNotifications from "../../Components/ActionNotifications";
import ErrorMessage from "../../Components/ErrorMessage";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import useFetch from "../../Hooks/useFetch";
import useGetDeliveryLocations from "../../Hooks/useGET/useGetDeliveryLocations";
import { getUsersWorkCenter, checkRoles, getUserId } from "../../Utils/helper";
import "./styles.css";
import DenyOrders from "../../Components/DenyOrders/denyOrders";

/*
-=-=-=-=-=-=-=-=-=-=-=-= Orders Page -=-=-=-=-=-=-=-=-=-=-=-=
=-=-=-=-=-=-=-= https://atlasboard.wsu-idp.org/orders/${num} =-=-=-=-=-=-=-
*/
const stepKey = [
  { value: 0, text: "New Order", id: 0 },
  { value: 1, text: "Supervisor Approved", id: 1 },
  { value: 2, text: "PCard Approved", id: 2 },
  { value: 3, text: "Order Processing", id: 15 },
  { value: 4, text: "Ordered", id: 6 },
  { value: 5, text: "Shipped", id: 7 },
  { value: 6, text: "Out for Delivery", id: 8 },
  { value: 7, text: "Delivered", id: 9 },
];
export default function OrderDetails() {
  const workcenter = getUsersWorkCenter()?.name || "",
    navigate = useNavigate(),
    params = useParams(),
    fetch = useFetch(navigate),
    [notifyResponse, setNotifications] = useState(null),
    { data, mutate, error } = useSWR(
      `${apiBaseURL}/api/Orders/Order?id=${params.orderId}`,
    ),
    order = data?.data[0] || null,
    { deliveryLocations } = useGetDeliveryLocations();

  const [showPopup, setPopup] = useState({
    provideAssistance: null,
    requestAssistance: null,
    orderDeny: null,
  });

  const fetchSuccessful = (response) => {
    setNotifications(response);
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const handlePopup = useCallback((popupKey, data) => {
    console.log("handlePopup data", data);
    setPopup((prevState) => ({
      ...prevState,
      [popupKey]: { data },
    }));
  }, []);

  const handleClose = (popupKey) => {
    setPopup((prevState) => ({
      ...prevState,
      [popupKey]: null,
    }));
  };
  const renderGrid = () => {
    const date = new Date(order.orderedDate).toLocaleString("en-US");
    const customTitle = (e) => {
      return <h5 className="white">{e.name}</h5>;
    };
    const customItem = (e) => {
      const dataGridSource = order?.vendors?.find((x) => x.id === e.id)?.items;

      return (
        <>
          <ApprovalStatusBar
            data={order}
            status={order.status.id}
            setNotifications={setNotifications}
            mutate={mutate}
          />

          <DataGrid
            dataSource={dataGridSource}
            showBorders={true}
            columnAutoWidth={true}
            maxHeight="300px"
            className="custom-grid"
          >
            <Column
              dataField="itemNumber"
              cellRender={cellRender}
              allowEditing={false}
            />
            <Column dataField="itemName" caption="Item" allowEditing={false} />
            <Column
              dataField="deliveryLocationId"
              caption="Delivery Location"
              allowEditing={false}
            >
              <Lookup
                dataSource={deliveryLocations}
                valueExpr="id"
                displayExpr="location"
              />
            </Column>
            <Column dataField="pricePerItem" allowEditing={false} />
            <Column dataField="quantity" allowEditing={false} />
            <Column dataField="billingCode" allowEditing={false} />
            <Column dataField="notes" allowEditing={false} />
          </DataGrid>
        </>
      );
    };

    const onRowRemoved = async () => {
      const response = await fetch.delete(
        `${apiBaseURL}/api/Orders?id=${params.orderId}`,
      );
      if (fetchSuccessful(response)) {
        mutate();
      }
    };
    return (
      <>
        {showPopup.provideAssistance && (
          <ProvideAssistance
            orderId={showPopup.provideAssistance.data}
            setNotifications={setNotifications}
            onClose={() => handleClose("provideAssistance")}
          />
        )}
        {showPopup.requestAssistance && (
          <RequestAssistance
            orderId={showPopup.requestAssistance.data}
            setNotifications={setNotifications}
            onClose={() => handleClose("requestAssistance")}
          />
        )}
        {showPopup.orderDeny && (
          <DenyOrders
            orderId={showPopup.orderDeny.data}
            setNotifications={setNotifications}
            onClose={() => handleClose("orderDeny")}
          />
        )}
        <div className="container">
          <div className="title-container row justify-content-between">
            <div className="col-auto me-auto white">
              <div className="order-name">{`Order ${params.orderId}`}</div>
              <div>{`Ordered by ${order.orderedBy.name} on ${date}`}</div>
            </div>
            <Button
              className="col-auto"
              icon="info"
              text="Provide Assistance"
              type="default"
              stylingMode="contained"
              visible={checkRoles(6) && order.status === 4}
              onClick={() => handlePopup("provideAssistance", params.orderId)}
            />
            <Button
              className="col-auto"
              icon="help"
              text="Request Assistance"
              type="default"
              stylingMode="contained"
              visible={
                workcenter === "Operations" &&
                order.status >= 2 &&
                order.status <= 5
              }
              onClick={() => handlePopup("requestAssistance", params.orderId)}
            />
            <Button
              className="col-auto"
              width={10}
              icon="trash"
              type="normal"
              stylingMode="text"
              visible={!order.status === 11 || !order.status === 9}
              onClick={onRowRemoved}
            />
            <Button
              style={{ margin: "0 10px" }}
              className="col-auto"
              type="danger"
              text="Deny"
              stylingMode="contained"
              onClick={() => handlePopup("orderDeny", params.orderId)}
              visible={
                order.status != 10 &&
                getUserId() === "d4ec0560-d7b0-4385-81a3-ef5ac53df4da"
              }
            />
          </div>

          <div className="stats white">
            <div>
              <div className="sub-title">Tracking Number</div>
              <div className="">
                {/* tracking number */}
                {order?.vendors?.length == 1 &&
                order?.vendors[0]?.trackingNumber ? (
                  <h5>
                    <span className="noBold label label-warning">
                      {`${order.vendors[0].trackingNumber}`}
                    </span>
                  </h5>
                ) : (
                  <h4>Not Available</h4>
                )}
              </div>
            </div>
            <div>
              <div className="sub-title">Urgency</div>
              <div className="stat-value urgency-box">
                <div className="urgency-level">
                  {renderUrgency(order.urgency)}
                </div>
                {order.urgencyJustification && (
                  <div className="urgency-justification">
                    {order.urgencyJustification}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="sub-title">Status</div>
              <div className="stat-value">{renderStatus(order.status)}</div>
            </div>
          </div>

          <Accordion
            dataSource={order.vendors}
            collapsible={true}
            multiple={true}
            animationDuration={300}
            itemTitleRender={customTitle}
            itemRender={customItem}
            id="accordion-container"
          />
        </div>
      </>
    );
  };

  if (!data) return <LoadingSpinnerModal show />;

  return (
    <div className="px-lg-4 page-content">
      <ActionNotifications response={notifyResponse} />
      <ChatBox
        id={params.orderId}
        getURL={`/api/Order/Comments?orderId=${params.orderId}`}
        postURL={"/api/Order/Comments"}
      />
      <div className="px-4">
        <div id="link">
          <Link className="hoverLinkText white bold" to="/orders" end>
            <h5 className="mb-3">&#x2190; All Orders</h5>
          </Link>
        </div>
        <div className="contentBox mb-3 mt-3">
          {!error ? renderGrid() : <ErrorMessage />}
        </div>
      </div>
    </div>
  );
}
const cellRender = (e) => {
  return (
    <a href={e.data.url} target="_blank" rel="noreferrer">
      {e.data.itemNumber}
    </a>
  );
};
